/**
 * Attack Paths — Track 6D toxic-combination engine view.
 *
 * Renders the output of /api/cdm/attack-paths as a left-panel finding
 * list + right-panel ReactFlow graph. Nodes are colour-coded by step
 * severity, edges carry the relation kind (exposed-as / exploits /
 * grants / reaches / chains-to). Read-only — clicking "Open evidence"
 * just console.logs the step's evidence dict for now.
 *
 * The engine is hand-coded patterns in backend/cdm/toxic_combinations.py;
 * the LLM rule-author is a separate later track.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Network, RefreshCw, ChevronLeft, AlertTriangle, ExternalLink, Sparkles,
} from 'lucide-react'
import { clsx } from 'clsx'
import ReactFlow, {
  Background, Controls, MarkerType,
  type Edge, type Node,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { api } from '../lib/api'

interface Step {
  kind: string
  label: string
  severity: string
  asset_id: string | null
  evidence: Record<string, unknown>
}

interface AttackPath {
  id: string
  title: string
  score: number
  narrative: string
  steps: Step[]
}

interface Resp {
  org_id: string
  generated_at: string
  count: number
  paths: AttackPath[]
}

const SEVERITY_NODE_BG: Record<string, string> = {
  critical: '#7f1d1d',
  high:     '#9a3412',
  medium:   '#1e3a8a',
  low:      '#064e3b',
  info:     '#1e293b',
}

const SEVERITY_NODE_BORDER: Record<string, string> = {
  critical: '#f43f5e',
  high:     '#f59e0b',
  medium:   '#3b82f6',
  low:      '#10b981',
  info:     '#94a3b8',
}

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-rose/10 text-rose-light border-rose/30',
  high:     'bg-amber/10 text-amber-light border-amber/30',
  medium:   'bg-cyan/10 text-cyan border-cyan/30',
  low:      'bg-emerald/10 text-emerald border-emerald/30',
  info:     'bg-text-muted/10 text-text-muted border-border',
}

function relationLabel(prevKind: string, nextKind: string): string {
  if (prevKind === 'entry' && (nextKind === 'control_gap' || nextKind === 'privilege')) return 'exposed-as'
  if (nextKind === 'impact')    return 'exploits'
  if (nextKind === 'privilege') return 'grants'
  if (nextKind === 'lateral')   return 'reaches'
  if (nextKind === 'exfil')     return 'exfils-via'
  return 'chains-to'
}

function buildGraph(path: AttackPath): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = path.steps.map((s, i) => ({
    id: `${path.id}#${i}`,
    type: 'default',
    position: { x: i * 240, y: 0 },
    data: {
      label: (
        <div className="text-left leading-tight">
          <div className="text-[9px] uppercase tracking-widest opacity-80">{s.kind.replace('_', ' ')}</div>
          <div className="text-xs font-medium mt-0.5">{s.label}</div>
        </div>
      ),
    },
    style: {
      background: SEVERITY_NODE_BG[s.severity] || SEVERITY_NODE_BG.info,
      border: `1px solid ${SEVERITY_NODE_BORDER[s.severity] || SEVERITY_NODE_BORDER.info}`,
      color: '#f8fafc',
      borderRadius: 6,
      padding: 8,
      width: 200,
    },
  }))
  const edges: Edge[] = path.steps.slice(1).map((s, i) => ({
    id: `${path.id}-e${i}`,
    source: `${path.id}#${i}`,
    target: `${path.id}#${i + 1}`,
    label: relationLabel(path.steps[i].kind, s.kind),
    labelStyle: { fontSize: 10, fill: '#94a3b8' },
    labelBgStyle: { fill: '#0f172a' },
    style: { stroke: '#475569' },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#475569' },
  }))
  return { nodes, edges }
}

export default function AttackPathsPage() {
  const [data, setData] = useState<Resp | null>(null)
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const r = await api.get('/api/cdm/attack-paths', { params: { limit: 50 } })
      setData(r.data as Resp)
      if ((r.data as Resp).paths.length && !focusedId) {
        setFocusedId((r.data as Resp).paths[0].id)
      }
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'failed to load'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  const focused = useMemo(() => {
    if (!data || !focusedId) return null
    return data.paths.find(p => p.id === focusedId) || null
  }, [data, focusedId])

  const graph = useMemo(() => focused ? buildGraph(focused) : null, [focused])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Network size={18} className="text-cyan" /> ATTACK PATHS
        </h1>
        <span className="text-[10px] text-text-muted">
          Toxic-combination engine · 8 hand-coded patterns · CAASM-grade chain detection
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => void load()} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="card p-2 text-[11px] text-rose-light flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {!loading && data && data.paths.length === 0 && (
        <div className="card p-4 text-text-muted text-sm flex items-start gap-3">
          <Sparkles size={16} className="text-cyan flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-text-primary font-medium mb-1">No attack paths found</div>
            <div className="text-[12px]">
              Either you&apos;re in great shape or the CDM hasn&apos;t ingested enough
              data yet. Try <code className="text-cyan">absorb_many</code> on at
              least one adapter — Shodan, F5, CrowdStrike, or Entra are good
              first picks.
            </div>
          </div>
        </div>
      )}

      {data && data.paths.length > 0 && (
        <div className="grid grid-cols-12 gap-4">
          {/* Left panel — list of paths */}
          <div className="col-span-4 card p-2 max-h-[78vh] overflow-y-auto">
            <div className="text-[10px] uppercase tracking-widest text-text-muted px-2 pb-2">
              {data.count} path(s) · top {data.paths.length}
            </div>
            <div className="space-y-1">
              {data.paths.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setFocusedId(p.id)}
                  className={clsx(
                    'w-full text-left p-2 rounded border transition-colors',
                    p.id === focusedId
                      ? 'bg-surface-raised border-cyan/40'
                      : 'bg-surface-card border-border hover:bg-surface-raised',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] text-text-primary font-medium truncate flex-1">
                      {p.title}
                    </div>
                    <span className={clsx(
                      'text-[10px] font-mono px-2 py-0.5 rounded border flex-shrink-0',
                      p.score >= 70 ? SEVERITY_BADGE.critical :
                      p.score >= 50 ? SEVERITY_BADGE.high :
                      p.score >= 25 ? SEVERITY_BADGE.medium :
                      SEVERITY_BADGE.low,
                    )}>
                      {p.score}
                    </span>
                  </div>
                  <div className="text-[10px] text-text-muted mt-1">
                    {p.steps.length} step(s) · {p.steps.map(s => s.kind).join(' → ')}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right panel — focused path graph */}
          <div className="col-span-8 space-y-2">
            {focused && (
              <>
                <div className="card p-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-sm font-bold text-text-primary flex-1">
                      {focused.title}
                    </h2>
                    <span className={clsx(
                      'text-[11px] font-mono px-2 py-0.5 rounded border',
                      focused.score >= 70 ? SEVERITY_BADGE.critical :
                      focused.score >= 50 ? SEVERITY_BADGE.high :
                      focused.score >= 25 ? SEVERITY_BADGE.medium :
                      SEVERITY_BADGE.low,
                    )}>
                      score {focused.score}/100
                    </span>
                  </div>
                  <div className="text-[12px] text-text-secondary mt-2">
                    {focused.narrative}
                  </div>
                </div>

                {/* Graph */}
                <div className="card p-0 overflow-hidden" style={{ height: '380px' }}>
                  {graph && (
                    <ReactFlow
                      nodes={graph.nodes}
                      edges={graph.edges}
                      fitView
                      fitViewOptions={{ padding: 0.3 }}
                      nodesDraggable={false}
                      nodesConnectable={false}
                      panOnDrag
                      zoomOnScroll
                      proOptions={{ hideAttribution: true }}
                    >
                      <Background color="#1e293b" gap={16} />
                      <Controls showInteractive={false} />
                    </ReactFlow>
                  )}
                </div>

                {/* Step evidence */}
                <div className="card p-3 space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-text-muted">
                    Steps + evidence
                  </div>
                  {focused.steps.map((s, i) => (
                    <div key={i} className="border border-border rounded p-2 bg-surface-raised">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-text-muted font-mono w-6">#{i + 1}</span>
                        <span className={clsx(
                          'text-[10px] px-2 py-0.5 rounded border w-20 text-center',
                          SEVERITY_BADGE[s.severity] || SEVERITY_BADGE.info,
                        )}>
                          {s.severity}
                        </span>
                        <span className="text-[10px] text-text-muted uppercase tracking-wider">
                          {s.kind.replace('_', ' ')}
                        </span>
                        <span className="text-[11px] text-text-primary flex-1">
                          {s.label}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            // Read-only view for now — write-back actions are
                            // deferred. Drop the evidence dict to the console
                            // so devs / power users can inspect it.
                            // eslint-disable-next-line no-console
                            console.log('[attack-path evidence]', {
                              path_id: focused.id, step: i, ...s,
                            })
                          }}
                          className="btn-ghost text-[10px]"
                        >
                          <ExternalLink size={10} /> Open evidence
                        </button>
                      </div>
                      {s.asset_id && (
                        <div className="text-[10px] text-text-muted font-mono mt-1 truncate">
                          asset: {s.asset_id}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
